import {
  Check,
  ChevronsUpDown,
  Paperclip,
  SendIcon,
  Thermometer,
  LoaderPinwheel,
  X,
  AlertTriangle,
  Square,
  Brain,
  Files,
  Upload
} from "lucide-react";
import React, { KeyboardEvent, useRef, useState, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle, useContext } from "react";
import { Message } from "../../interfaces/chat.interface";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Slider } from "../ui/slider";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { CHAT_CONFIG, FILE_UPLOAD_CONFIG } from "@/constants/chat.constant";
import { ConversationType } from "@/constants/chat.constant";
import { FileAttachment } from "./attachment";
import { useIsMobile } from "@/hooks/use-mobile";
import { CreditsContext } from "@/store/credits.store";
import { FileAttachmentWrapper } from "./attachement-wrapper";
import { Popover, PopoverTrigger } from "../ui/popover";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { TemperatureDropdown } from "./temperature-settings";
import { ReasoningEffort } from "./reasoning-effort";

/**
 * Interface for selected file with metadata
 */
interface SelectedFile {
  file: File;
  id: string;
  showRemove: boolean;
}

/**
 * Props for the ChatInput component
 */
interface Props {
  /** Current input content value */
  content: string | undefined;
  /** Active conversation messages */
  activeConversation: Array<Message> | null;
  /** Whether a response is currently being generated */
  isResponseGenerating: boolean;
  /** Available AI models to select from */
  models?: string[];
  /** Currently selected AI model */
  selectedModel?: string;
  /** Current temperature setting */
  temperature?: number;
  /** Current reasoning effort setting */
  effort?: number;
  /** ID of the selected conversation */
  selectedConversationId?: string;
  /** Callback fired when message is sent */
  onSend: (message: Message) => void;
  /** Callback fired when input value changes */
  onInputChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  /** Callback fired on keyboard events */
  onKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  /** Callback fired when user starts typing */
  onStartTyping: () => void;
  /** Callback fired when user stops typing */
  onStopTyping: () => void;
  /** Callback fired when model selection changes */
  onModelChange?: (model: string) => void;
  /** Callback fired when files are attached */
  onAttach?: (files: FileList) => void;
  /** Callback fired when temperature changes */
  onTemperatureChange?: (value: number) => void;
  /** Callback fired when reasoning effort changes */
  onReasoningEffortChange?: (value: number) => void;
  /** Callback fired to cancel streaming response */
  onCancelStream: () => void;
  /** Whether running on mobile device */
  mobile?: boolean;
  /** Function to retrieve files for a conversation */
  getFilesForConversation?: (conversationId: string) => File[];
  /** Function to update files for a conversation */
  updateFilesForConversation?: (conversationId: string, files: File[]) => void;
  /** Type of conversation */
  conversationType: ConversationType;
}

/**
 * Ref interface for ChatInput component methods
 */
export interface ChatInputRef {
  /** Focus the input field */
  focus: () => void;
  /** Focus the input field and move cursor to end */
  focusAtEnd: () => void;
}

/**
 * ChatInput component provides a text input area with file upload, model selection,
 * temperature/effort controls, and message sending functionality. Supports file
 * attachments with validation, auto-resizing textarea, and keyboard shortcuts.
 * 
 * @component
 * @param {Props} props - Component props
 * @param {string | undefined} props.content - Current input content
 * @param {Array<Message> | null} props.activeConversation - Active conversation
 * @param {boolean} props.isResponseGenerating - Whether response is generating
 * @param {string[]} [props.models] - Available models
 * @param {string} [props.selectedModel] - Selected model
 * @param {number} [props.temperature] - Temperature setting
 * @param {number} [props.effort] - Reasoning effort setting
 * @param {string} [props.selectedConversationId] - Selected conversation ID
 * @param {(message: Message) => void} props.onSend - Send message callback
 * @param {(e: React.ChangeEvent<HTMLTextAreaElement>) => void} props.onInputChange - Input change callback
 * @param {(e: KeyboardEvent<HTMLTextAreaElement>) => void} props.onKeyDown - Key down callback
 * @param {() => void} props.onStartTyping - Start typing callback
 * @param {() => void} props.onStopTyping - Stop typing callback
 * @param {(model: string) => void} [props.onModelChange] - Model change callback
 * @param {(files: FileList) => void} [props.onAttach] - File attach callback
 * @param {(value: number) => void} [props.onTemperatureChange] - Temperature change callback
 * @param {(value: number) => void} [props.onReasoningEffortChange] - Effort change callback
 * @param {() => void} props.onCancelStream - Cancel stream callback
 * @param {boolean} [props.mobile] - Mobile device flag
 * @param {(conversationId: string) => File[]} [props.getFilesForConversation] - Get files function
 * @param {(conversationId: string, files: File[]) => void} [props.updateFilesForConversation] - Update files function
 * @returns {JSX.Element} Chat input component with controls
 * 
 * @example
 * ```tsx
 * <ChatInput
 *   content={content}
 *   onSend={handleSend}
 *   onInputChange={handleChange}
 *   isResponseGenerating={isGenerating}
 * />
 * ```
 */
const ChatInput = forwardRef<ChatInputRef, Props>(({
  content,
  activeConversation,
  onSend,
  onInputChange,
  onKeyDown,
  onStartTyping,
  onStopTyping,
  isResponseGenerating,
  selectedModel,
  onModelChange = () => { },
  onAttach = () => { },
  temperature = CHAT_CONFIG.TEMPERATURE.DEFAULT,
  effort = CHAT_CONFIG.REASONING_EFFORT.DEFAULT,
  onTemperatureChange = () => { },
  onReasoningEffortChange = () => { },
  selectedConversationId,
  getFilesForConversation,
  updateFilesForConversation,
  onCancelStream,
  conversationType
}, ref) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isMobile = useIsMobile();

  useImperativeHandle(ref, () => ({
    focus: () => {
      // Only focus on web, not on mobile
      if (!isMobile) {
        textareaRef.current?.focus();
      }
    },
    focusAtEnd: () => {
      // Only focus on web, not on mobile
      if (!isMobile) {
        const textarea = textareaRef.current;
        if (textarea) {
          textarea.focus();
          const length = textarea.value.length;
          textarea.setSelectionRange(length, length);
        }
      }
    },
  }));
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const dragDepthRef = React.useRef(0);
  const prevIsResponseGenerating = useRef(isResponseGenerating);
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
  const [showFileSizeWarning, setShowFileSizeWarning] = useState(false);
  const [showFileCountWarning, setShowFileCountWarning] = useState(false);
  const [showFileTypeWarning, setShowFileTypeWarning] = useState(false);
  const [fileTypeWarningMessage, setFileTypeWarningMessage] = useState("");
  const { state } = useContext(CreditsContext);
  const [models, setModels] = useState<any[]>([]);

  // Calculate total file size and check if it exceeds limit
  const totalFileSize = useMemo(() => {
    return selectedFiles.reduce((sum, sf) => sum + sf.file.size, 0);
  }, [selectedFiles]);


  const exceedsFileSizeLimit = useMemo(() => {
    return totalFileSize > FILE_UPLOAD_CONFIG.MAX_TOTAL_SIZE;
  }, [totalFileSize]);

  const exceedsFileCountLimit = useMemo(() => {
    return selectedFiles.length > FILE_UPLOAD_CONFIG.MAX_FILE_COUNT;
  }, [selectedFiles.length]);

  useEffect(() => {
    setModels(
      state.model_groups
        .filter(m => Boolean(m.name)) // remove null/undefined names
        .map(m => {
          const monthly = m.monthly_credits ?? 0;
          const used = m.usage ? m.usage.used_tokens ?? 0 : 0;

          let enabled;

          if (monthly === 0) {
            enabled = false;
          } else if (monthly === -1) {
            enabled = true;
          } else {
            enabled = (monthly - used) > 0;
          }

          return {
            name: m.name!,
            enabled,
          };
        })

    );
  }, [state.model_groups]);


  // Show warning when files exceed limit
  useEffect(() => {
    if (exceedsFileSizeLimit) {
      setShowFileSizeWarning(true);
    } else {
      setShowFileSizeWarning(false);
    }
  }, [exceedsFileSizeLimit]);

  // Show warning when file count exceeds limit
  useEffect(() => {
    if (exceedsFileCountLimit) {
      setShowFileCountWarning(true);
    } else {
      setShowFileCountWarning(false);
    }
  }, [exceedsFileCountLimit]);

  const handleSend = useCallback(() => {
    if (!content?.trim() || isResponseGenerating || exceedsFileSizeLimit || exceedsFileCountLimit) return;

    const message: Message = {
      content: content.trim(),
      role: "user",
      selectedFiles:
        selectedFiles.length > 0
          ? selectedFiles.map((sf) => sf.file)
          : undefined,
    };

    onSend(message);
    setSelectedFiles([]);

    // Clear persisted files after sending
    if (updateFilesForConversation && selectedConversationId) {
      const conversationKey = selectedConversationId || "new";
      updateFilesForConversation(conversationKey, []);
    }
  }, [content, isResponseGenerating, exceedsFileSizeLimit, exceedsFileCountLimit, onSend, selectedFiles, updateFilesForConversation, selectedConversationId]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();

        const isMobile = () => {
          const userAgent =
            typeof window.navigator === "undefined" ? "" : navigator.userAgent;
          const mobileRegex =
            /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i;
          return mobileRegex.test(userAgent);
        };

        if (!isMobile() && !isResponseGenerating) {
          handleSend();
          return;
        }
      }

      onKeyDown(e);
    },
    [handleSend, onKeyDown, isResponseGenerating]
  );

  // Helper function to get file extension
  const getFileExtension = (fileName: string): string => {
    return fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
  };

  // Helper function to check if file is allowed
  const isFileAllowed = (file: File): boolean => {
    const extension = getFileExtension(file.name);
    const isBlocked = FILE_UPLOAD_CONFIG.BLOCKED_EXTENSIONS.includes(extension);
    const isAllowed = FILE_UPLOAD_CONFIG.ALLOWED_EXTENSIONS.includes(extension);
    return !isBlocked && isAllowed;
  };

  // Helper function to turn File[] into SelectedFile[]
  const buildSelectedFiles = (files: File[]): SelectedFile[] => {
    const now = Date.now();
    return files.map((file) => ({
      file,
      id: `${file.name}-${now}-${Math.random()}`,
      showRemove: true,
    }));
  };

  // Helper function to validate files against allowed extensions
  const validateFilesOrWarn = (files: File[]): boolean => {
    const invalidFiles = files.filter((file) => !isFileAllowed(file));
    if (invalidFiles.length > 0) {
      const invalidExtensions = invalidFiles
        .map((f) => getFileExtension(f.name))
        .join(", ");
      setFileTypeWarningMessage(`File type not allowed: ${invalidExtensions}`);
      setShowFileTypeWarning(true);
      return false;
    }
    setShowFileTypeWarning(false);
    return true;
  };

  // Persist to conversation and notify parent onAttach
  const persistAndNotify = (updatedSelectedFiles: SelectedFile[], notifyFiles?: FileList | File[]) => {
    if (updateFilesForConversation && selectedConversationId) {
      const conversationKey = selectedConversationId || "new";
      updateFilesForConversation(conversationKey, updatedSelectedFiles.map((sf) => sf.file));
    }

    // onAttach expects FileList (from input) but we also support File[] for drop/paste
    if (notifyFiles && onAttach) {
      // TODO: Review this code if onAttach will be used. Currently, onAttach = () => {}
      if (Array.isArray(notifyFiles) && notifyFiles.every((item) => item instanceof File)) {
        // case: File[] for drag-and-drop or copy-and-paste
        const dt = new DataTransfer();
        for (let i = 0; i < notifyFiles.length; i++) {
          dt.items.add(notifyFiles[i]);
        }
        onAttach(dt.files);
      } else {
        // case: FileList for Attach button
        onAttach(notifyFiles as FileList);
      }
    }
  };

  // Helper function to generate a unique filename for clipboard images/blobs
  const generateClipboardFilename = (mime: string, base: string = "pasted"): string => {
    // Derive extension from MIME if possible
    const extMap: Record<string, string> = {
      "image/png": ".png",
      "image/jpeg": ".jpg",
      "image/jpg": ".jpg",
      "image/gif": ".gif",
      "image/webp": ".webp",
      "application/pdf": ".pdf",
    };
    const ext = extMap[mime] || "";
    const ts = new Date().toISOString().replace(/[:.]/g, "-");
    const rand = Math.random().toString(36).slice(2, 8);
    return `${base}-${ts}-${rand}${ext}`;
  };

  // Helper function to extract files from clipboard and generate filenames
  const filesFromClipboard = (cd: DataTransfer): File[] => {
    const result: File[] = [];
    for (const item of Array.from(cd.items ?? [])) {
      if (item.kind === "file") {
        const blob = item.getAsFile();
        if (blob) {
          // If blob has "image.png" or empty name, replace with our generated name
          const needsName =
            !blob.name ||
            blob.name === "image.png" ||
            blob.name === "image.jpg" ||
            blob.name === "image.jpeg";

          if (needsName) {
            const filename = generateClipboardFilename(blob.type || "image/png");
            const fileWithName = new File([blob], filename, { type: blob.type || "image/png" });
            result.push(fileWithName);
          } else {
            result.push(blob);
          }
        }
      }
    }
    return result;
  };

  // Handle file selection - allow files to be added, show warning if exceeds limit
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newFilesArray = Array.from(files);

    // Reject invalid types
    if (!validateFilesOrWarn(newFilesArray)) {
      e.target.value = "";
      return;
    }

    // Create SelectedFile objects - add files even if they exceed count limit
    const newFiles: SelectedFile[] = buildSelectedFiles(newFilesArray);

    // Use functional update to get current state and add new files
    setSelectedFiles((prev) => {
      const updatedFiles = [...prev, ...newFiles];
      // Persist files for current conversation
      persistAndNotify(updatedFiles, files);
      // Reset the input so the same file can be selected again
      e.target.value = "";
      return updatedFiles;
    });
  };

  // Remove file
  const handleRemoveFile = (id: string) => {
    setSelectedFiles((prev) => {
      const updated = prev.filter((f) => f.id !== id);

      // Update persistence
      if (updateFilesForConversation && selectedConversationId) {
        const conversationKey = selectedConversationId || "new";
        updateFilesForConversation(conversationKey, updated.map(sf => sf.file));
      }

      return updated;
    });
  };

  // Trigger file input click
  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  // Attach files using drag-and-drop
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    dragDepthRef.current = 0;
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    const newFilesArray = Array.from(files);

    // Reject invalid types
    if (!validateFilesOrWarn(newFilesArray)) {
      return;
    }

    // Create SelectedFile objects - add files even if they exceed count limit
    const newFiles = buildSelectedFiles(newFilesArray);

    // Use functional update to get current state and add new files
    setSelectedFiles((prev) => {
      const updatedFiles = [...prev, ...newFiles];
      // Persist files for current conversation
      persistAndNotify(updatedFiles, newFilesArray);
      return updatedFiles;
    });
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragDepthRef.current += 1 ;
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragDepthRef.current -= 1;
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0;
      setIsDragging(false);
    }
  };

  // Attach files using copy-and-paste
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const cd = e.clipboardData;
    const filesArray = filesFromClipboard(cd);

    // If there are no files in the clipboard, let text paste proceed normally.
    if (filesArray.length === 0) {
      return; // do not call preventDefault; allow text paste
    }

    // Reject invalid types
    if (!validateFilesOrWarn(filesArray)) {
      return;
    }

    // Create SelectedFile objects - add files even if they exceed count limit
    const newFiles = buildSelectedFiles(filesArray);

    // Use functional update to get current state and add new files
    setSelectedFiles((prev) => {
      const updatedFiles = [...prev, ...newFiles];
      // Persist files for current conversation
      persistAndNotify(updatedFiles, filesArray);
      return updatedFiles;
    });
  }

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = "auto";
    textarea.style.height =
      Math.min(textarea.scrollHeight, CHAT_CONFIG.TEXTAREA.MAX_HEIGHT) + "px";
  }, [content]);

  useEffect(() => {
    if (prevIsResponseGenerating.current && !isResponseGenerating) {
      // Only autofocus on web, not on mobile
      if (!isMobile) {
        const timeoutId = setTimeout(() => {
          textareaRef.current?.focus();
        }, 100);
        prevIsResponseGenerating.current = isResponseGenerating;
        return () => clearTimeout(timeoutId);
      }
    }
    prevIsResponseGenerating.current = isResponseGenerating;
  }, [isResponseGenerating, isMobile]);

  // Load and persist files when conversation changes
  useEffect(() => {
    const conversationKey = selectedConversationId || "new";

    if (getFilesForConversation && updateFilesForConversation) {
      // Load persisted files for this conversation
      const persistedFiles = getFilesForConversation(conversationKey);

      if (persistedFiles && persistedFiles.length > 0) {
        // Convert File[] to SelectedFile[]
        const filesWithIds: SelectedFile[] = persistedFiles.map((file) => ({
          file,
          id: `${file.name}-${Date.now()}-${Math.random()}`,
          showRemove: true
        }));
        setSelectedFiles(filesWithIds);
      } else {
        // Clear if no persisted files
        setSelectedFiles([]);
      }
    }
  }, [selectedConversationId, getFilesForConversation, updateFilesForConversation]);


  const cancelStreaming = () => {
    onCancelStream()
  };



  const showStopButton = (): boolean => {
    if (activeConversation) {
      const assistantIndex = activeConversation && activeConversation?.length;

      if (isResponseGenerating && activeConversation && assistantIndex && activeConversation[assistantIndex - 1] && (activeConversation[assistantIndex - 1].content?.length > 0)) {
        return true
      }
    }

    return false;
  };

  const disableStopButton = (): boolean => {
    if (activeConversation) {
      const assistantIndex = activeConversation && activeConversation?.length;

      if (
        activeConversation[assistantIndex - 1] &&
        activeConversation[assistantIndex - 1].content?.length > 0
      ) {
        if (activeConversation[assistantIndex - 1].isStreamingComplete) {
          return true;
        }
        return false;
      }
    }

    return false;
  };
  
  

  const canSend = Boolean(content?.trim() && !isResponseGenerating && !exceedsFileSizeLimit && !exceedsFileCountLimit);
  const isDisabled = isResponseGenerating || exceedsFileSizeLimit || exceedsFileCountLimit;

  const ModelDropdown = ({ isMobile = false }: { isMobile?: boolean }) => {
    const [isOpen, setIsOpen] = useState(false);

    const handleModelSelect = (model: string) => {
      onModelChange(model);
      setIsOpen(false);
    };

    return (
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        {!isMobile ? (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">Model :</span>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                aria-label="Select model"
                className="flex items-center gap-2 px-3 py-1.5 h-auto border-[#D4D4D8] hover:bg-gray-50"
              >
                <span className="font-medium text-gray-700">
                  {selectedModel}
                </span>
                <ChevronsUpDown size={16} className="text-[#71717B]" />
              </Button>
            </DropdownMenuTrigger>
          </div>
        ) : (
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              aria-label="Select model"
              className="rounded-full border-[#D4D4D8] text-[#005587] hover:text-[#005587] hover:bg-gray-50"
            >
              <ChevronsUpDown size={16} />
            </Button>
          </DropdownMenuTrigger>
        )}
        <DropdownMenuContent align="start" className="min-w-[140px] z-[100]">
          {models?.map((model) => (
            <DropdownMenuItem
              key={model.name}
              disabled={!model.enabled}
              onClick={() => handleModelSelect(model.name)}
              className="flex items-center justify-between cursor-pointer"
            >
              <span>{model.name}</span>
              {model.name === selectedModel && (
                <Check size={16} className="text-blue-600" />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const dropZoneClassName = "relative w-full border rounded-xl p-2 transition-colors" + (
    isDragging ? "border-dashed border-primary bg-muted/50"
    : "border-input bg-background"
  );

  return (
    <div className="chat-input w-full bg-[#fefefe]">
      <div className="bg-secondary-500/5 pb-4 flex flex-col items-center">
        <div className="mx-4 mt-4 md:mx-4 md:last:mb-6 md:max-w-[56rem] w-full">
          {/* File size warning banner */}
          {showFileSizeWarning && exceedsFileSizeLimit && (
            <div className="mb-3 flex justify-center">
              <div className="bg-[#FFF1CD] border border-[#FFF1CD] rounded flex items-center gap-3 px-4 py-3">
                <AlertTriangle className="w-5 h-5 text-[#99460E] flex-shrink-0" />
                <p className="text-md text-[#99460E] font-secondary font-medium whitespace-nowrap">
                  The selected file is too large. Maximum file size allowed is{" "}
                  {FILE_UPLOAD_CONFIG.MAX_TOTAL_SIZE / (1024 * 1024)} MB
                </p>
                <button
                  onClick={() => setShowFileSizeWarning(false)}
                  className="ml-4 text-[#99460E] flex-shrink-0"
                  aria-label="Dismiss warning"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          )}
          {/* File count warning banner */}
          {showFileCountWarning && exceedsFileCountLimit && (
            <div className="mb-3 flex justify-center">
              <div className="bg-[#FFF1CD] border border-[#FFF1CD] rounded flex items-center gap-3 px-4 py-3">
                <AlertTriangle className="w-5 h-5 text-[#99460E] flex-shrink-0" />
                <p className="text-md text-[#99460E] font-secondary font-medium whitespace-nowrap">
                  Maximum file limit is {FILE_UPLOAD_CONFIG.MAX_FILE_COUNT}
                </p>
                <button
                  onClick={() => setShowFileCountWarning(false)}
                  className="ml-4 text-[#99460E] flex-shrink-0"
                  aria-label="Dismiss warning"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          )}
          {/* File type warning banner */}
          {showFileTypeWarning && fileTypeWarningMessage && (
            <div className="mb-3 flex justify-center">
              <div className="bg-[#FFF1CD] border border-[#FFF1CD] rounded flex items-center gap-3 px-4 py-3">
                <AlertTriangle className="w-5 h-5 text-[#99460E] flex-shrink-0" />
                <p className="text-md text-[#99460E] font-secondary font-medium whitespace-nowrap">
                  {fileTypeWarningMessage}
                </p>
                <button
                  onClick={() => setShowFileTypeWarning(false)}
                  className="ml-4 text-[#99460E] flex-shrink-0"
                  aria-label="Dismiss warning"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          )}
          <div className="w-full bg-white border rounded-lg shadow-sm cursor-text">
            <div
              className={dropZoneClassName}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
            >
              {/* File chips section with horizontal scroll */}
              {selectedFiles.length > 0 && (
                <div className="px-4 pt-3 pb-2 relative">
                  <FileAttachmentWrapper
                    isInput={true}
                    fileList={selectedFiles}
                    handleRemoveFile={(id) => handleRemoveFile(id)}
                  ></FileAttachmentWrapper>
                </div>
              )}

              {/* Text input section */}
              <div className="flex items-end px-4 py-2">
                <div className="flex-1">
                  <Textarea
                    ref={textareaRef}
                    className="w-full resize-none border-0 outline-0 text-base leading-relaxed text-black bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 p-0 shadow-none placeholder:text-[#005587] pt-[6px] custom-placeholder"
                    style={{
                      minHeight: `${CHAT_CONFIG.TEXTAREA.MIN_HEIGHT}px`,
                      maxHeight: `${CHAT_CONFIG.TEXTAREA.MAX_HEIGHT}px`,
                      boxShadow: "none",
                      border: "none",
                      fontSize: "16px",
                    }}
                    placeholder="How can I help you today ?"
                    value={content || ""}
                    rows={1}
                    disabled={isDisabled}
                    onCompositionStart={onStartTyping}
                    onCompositionEnd={onStopTyping}
                    onChange={onInputChange}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    aria-label="Type your message"
                  />
                </div>
              </div>

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileChange}
                className="hidden"
                multiple
                accept={FILE_UPLOAD_CONFIG.ALLOWED_EXTENSIONS.join(",")}
                aria-label="File upload input"
              />

              {/* Desktop layout */}
              <div className="hidden md:flex items-center justify-between px-4 py-3 border-t border-gray-100">
                <div className="flex items-center gap-2">
                  <ModelDropdown />
                  {!CHAT_CONFIG.TEMPERATURE.NOT_SUPPORTED_MODELS.includes(
                    selectedModel ?? " "
                  ) && (
                      <TemperatureDropdown
                        temperature={temperature}
                        onTemperatureChange={onTemperatureChange}
                      />
                    )}
                  {!CHAT_CONFIG.REASONING_EFFORT.NO_SUPPORTED_MODELS.includes(selectedModel ?? " ") &&
                    (<ReasoningEffort effort={effort} onEffortChange={onReasoningEffortChange} selectedModel={selectedModel} />)
                  }
                </div>

                <div className="flex items-center gap-2">
                  {!CHAT_CONFIG.MODELS.VISION_NOT_SUPPORTED.includes(selectedModel ?? " ") && <Button
                    variant="ghost"
                    onClick={handleAttachClick}
                    disabled={
                      exceedsFileSizeLimit ||
                      exceedsFileCountLimit ||
                      isResponseGenerating
                    }
                    aria-label="Attach file"
                    className="flex items-center gap-1 text-black hover:text-black hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Paperclip size={16} />
                    <span>Attach</span>
                  </Button>}

                  <Button
                    onClick={isResponseGenerating ? onStopTyping : handleSend}
                    disabled={!isResponseGenerating && !canSend}
                    size="icon"
                    aria-label={
                      isResponseGenerating ? "Generating" : "Send message"
                    }
                    className="rounded-full bg-[#005587] hover:bg-[#004070] text-white"
                  >
                    {isResponseGenerating ? (
                      <>
                        {showStopButton() ? (
                          <div
                            onClick={
                              !disableStopButton() ? cancelStreaming : undefined
                            }
                            className={
                              disableStopButton()
                                ? "cursor-not-allowed"
                                : "cursor-pointer"
                            }
                          >
                            <Square
                              size={16}
                              fill="white"
                              className="cursor-pointer"
                            />
                          </div>
                        ) : (
                          <LoaderPinwheel
                            size={16}
                            className="animate-spin disabled"
                          />
                        )}
                      </>
                    ) : (
                      <SendIcon size={16} />
                    )}
                  </Button>
                </div>
              </div>

              {/* Mobile layout */}
              <div className="flex md:hidden items-center justify-between px-4 py-3 border-t border-gray-100">
                <div className="flex items-center gap-2">
                  <ModelDropdown isMobile />

                  {!CHAT_CONFIG.MODELS.VISION_NOT_SUPPORTED.includes(selectedModel ?? " ") && <Button
                    variant="outline"
                    size="icon"
                    onClick={handleAttachClick}
                    disabled={exceedsFileSizeLimit || exceedsFileCountLimit || isResponseGenerating}
                    aria-label="Attach file"
                    className="rounded-full border-[#D4D4D8] text-[#005587] hover:text-[#005587] hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Paperclip size={16} />
                  </Button>}

                  {!CHAT_CONFIG.TEMPERATURE.NOT_SUPPORTED_MODELS.includes(
                    selectedModel ?? " "
                  ) && (
                      <TemperatureDropdown
                        temperature={temperature}
                        onTemperatureChange={onTemperatureChange}
                      />
                    )}
                  {!CHAT_CONFIG.REASONING_EFFORT.NO_SUPPORTED_MODELS.includes(selectedModel ?? " ")
                    &&
                    (<ReasoningEffort effort={effort} onEffortChange={onReasoningEffortChange} selectedModel={selectedModel} />)}
                </div>

                <div className="flex items-center">
                  <Button
                    onClick={isResponseGenerating ? onStopTyping : handleSend}
                    disabled={!isResponseGenerating && !canSend}
                    size="icon"
                    aria-label={
                      isResponseGenerating ? "Generating" : "Send message"
                    }
                    className="rounded-full bg-[#005587] hover:bg-[#004070] text-white"
                  >
                    {isResponseGenerating ? (
                      <>
                        {showStopButton() ? (
                          <div onClick={cancelStreaming}>
                            <Square
                              size={16}
                              fill="white"
                              className="cursor-pointer"
                            />
                          </div>
                        ) : (
                          <LoaderPinwheel
                            size={16}
                            className="animate-spin disabled"
                          />
                        )}
                      </>
                    ) : (
                      <SendIcon size={16} />
                    )}
                  </Button>
                </div>
              </div>
              {isDragging && (
                <div className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center rounded-xl">
                  {/* scrim */}
                  <div className="absolute inset-0 rounded-xl border-2 border-primary bg-white/60 backdrop-blur-[1px]" />
                  {/* centered message */}
                  <div className="relative flex flex-col items-center gap-2 px-4 py-3 text-center">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-background/95 shadow-lg">
                      <Upload className="h-7 w-7 text-primary-500 font-semibold" />
                    </div>
                    <div className="text-xl font-xl text-primary-500 font-semibold">
                      Drop files to attach
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div>
          <p className="text-center text-primary-500 text-xs leading-5 mt-3 md:mt-4 px-4 font-semibold">
            {
              conversationType === ConversationType.CHOP_GPT ?
              "AI can make mistakes, so it's important to double-check important information." :
              <>Be specific! Policies may vary by department, patient characteristics, etc.
              Include these details in your question for the most accurate response.<br />
              AI can make mistakes, so it's important to double-check important information.</>
            }
          </p>
        </div>
      </div>
    </div>
  );
});

ChatInput.displayName = "ChatInput";

export default ChatInput;
